<template>
	<div
		:class="[
			'flex flex-column relative input-content ',
			!noSpace ? 'gap-2' : null,
		]"
	>
		<!-- Label container -->
		<div class="flex gap-1" v-if="props.label">
			<label
				v-if="props.label"
				:for="props.name"
				:class="[
					'tv__input-label',
					props.size === 'sm' ? 'tv__input-sm-label' : '',
				]"
			>
				{{ props.label }}
				<span v-if="props.alias" class="tv__input-label-alias">
					{{ props.alias }}
				</span>
			</label>
			<!-- label tooltip -->
			<TVTooltip
				v-if="props.tooltipMessage"
				:max-width-tooltip="props.tooltipMaxWidth"
				:float-tooltip-mobile="props.tooltipDirMobile"
				:float-tooltip-desk="props.tooltipDirDesk"
			>
				<template #title>
					<p v-html="props.tooltipMessage" />
				</template>
			</TVTooltip>
		</div>

		<div class="relative">
			<!-- Input -->
			<InputText
				:id="props.id"
				:value="inputValue"
				:name="props.name"
				:type="props.type"
				unstyled
				:autocomplete="props.autocomplete"
				:aria-describedby="props.id + '-help'"
				:invalid="props.error"
				:class="inputClasses"
				:disabled="isInputDisabled"
				:placeholder="props.placeholder"
				@input="handleChange"
				@blur="handleBlur"
				class="notranslate"
				translate="no"
			/>
			<!-- Button clear input when have error -->
			<i
				v-show="errorMessage || customErrorMessage"
				:class="[
					'tv__input-btn',
					'tv__input-icon',
					'tv__input-icon-clear',
					props.size === 'sm' ? 'tv__input-sm' : '',
				]"
				@click="clearInput"
			/>
			<!-- Button for edit when input are disabled -->
			<i
				v-show="props.disabled && props.canEdit && isInputDisabled"
				:class="[
					'tv__input-btn',
					'tv__input-icon',
					'tv__input-icon-edit',
					props.size === 'sm' ? 'tv__input-sm' : '',
				]"
				@click="enableInput"
			/>
		</div>

		<span :class="[!noSpace ? 'input-message-content' : null]">
			<p
				style="margin-top: 0.5rem"
				v-if="props.helpMessage && !props.error"
				:class="[
					'tv__input-help',
					props.size === 'sm' ? 'tv__input-help-sm' : '',
				]"
			>
				{{ props.helpMessage }}
			</p>
			<!-- Error message -->
			<div
				style="margin-top: 0.5rem"
				v-if="errorMessage || customErrorMessage"
				:class="[
					'tv__input-error-message flex mt-0',
					props.size === 'sm' ? 'tv__input-error-message-sm' : '',
				]"
			>
				<i class="tv__input-icon tv__input-icon-error" />
				<p>
					{{ errorMessage || customErrorMessage }}
				</p>
			</div>
		</span>
	</div>
</template>

<script setup>
import { defineProps, ref, computed, defineEmits, toRef, watch } from 'vue'
import { useField } from 'vee-validate'
import InputText from 'primevue/inputtext'
import TVTooltip from '../global/TVTooltip.vue'

const props = defineProps({
	type: {
		type: String,
		default: 'text',
	},

	name: {
		type: String,
		required: true,
	},
	label: String,
	alias: String,
	placeholder: String,
	id: String,
	modelValue: String,
	disabled: Boolean,
	canEdit: Boolean,
	helpMessage: String,
	noSpace: Boolean,
	tooltipMessage: String,
	tooltipDirDesk: String,
	tooltipDirMobile: String,
	tooltipMaxWidth: Number,
	error: Boolean,
	customErrorMessage: String,
	autocomplete: String,
	size: {
		type: String,
		default: 'lg',
		validator: (value) => ['lg', 'sm'].includes(value),
	},
})

const emits = defineEmits(['update:modelValue', 'update:withEmptyValue'])

const nameRef = computed(() => props.name ?? 'defaultFieldName')

const {
	value: inputValue,
	errorMessage,
	handleBlur,
	handleChange,
	meta,
} = useField(nameRef, undefined, { initialValue: props.modelValue ?? '' })

const isInputDisabled = ref(props.disabled)

const hasError = computed(() => {
	return (
		(!meta.valid && errorMessage.value !== undefined) ||
		props.error ||
		props.customErrorMessage
	)
})

const inputClasses = computed(() => [
	'tv__input',
	hasError.value ? 'tv__input-error' : null,
	props.canEdit ? 'tv__input-edit' : null,
	props.size === 'sm' ? 'tv__input-sm' : null,
])

const clearInput = () => {
	inputValue.value = ''
	emits('update:modelValue', '')
}

const enableInput = () => {
	isInputDisabled.value = false
}

watch(
	() => props.modelValue,
	(newValue) => {
		if (newValue && newValue !== '') {
			inputValue.value = newValue
		}
	},
	{ immediate: true },
)
watch(
	() => props.disabled,
	(newValue) => {
		isInputDisabled.value = newValue
	},
	{ immediate: true },
)

watch(inputValue, (newValue) => {
	if (props.type === 'text') {
		const trimmedValue = newValue.trim()
		inputValue.value = trimmedValue
		emits('update:modelValue', trimmedValue)
		emits('update:withEmptyValue', trimmedValue)
	}
	emits('update:modelValue', newValue)
	emits('update:withEmptyValue', newValue)
})
</script>
